import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { renderAsync } from 'docx-preview';
import { replaceVariablesInHTML } from '../utils/docxParser';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { API_BASE_URL } from '../config';
import RupiahInput from '../components/RupiahInput';
import { parseDigits } from '../utils/currency';
import VariableInput from '../components/VariableInput';
import AssignmentCommentInput from '../components/AssignmentCommentInput';
import UploadDocumentModal, { UploadDocFormData } from '../components/UploadDocumentModal';

const NewAgreementRequest: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    pid: '',
    templateType: 'Internal',
    categoryId: '',
    agreementTypeId: '',
    companyId: '',
    templateId: '',
    agreementValue: '',
    paymentMethod: '',
    period: '',
    guarantee: ''
  });

  const [categories, setCategories] = useState<any[]>([]);
  const [agreementTypes, setAgreementTypes] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [template, setTemplate] = useState<any>(null);
  const [availableTemplatesMain, setAvailableTemplatesMain] = useState<any[]>([]);
  const [loadingTemplatesMain, setLoadingTemplatesMain] = useState(false);
  const [variables, setVariables] = useState<any[]>([]);
  const [userGroups, setUserGroups] = useState<number[]>([]);
  const [supportingDocuments, setSupportingDocuments] = useState<any[]>([]);
  const [showUploadDocModal, setShowUploadDocModal] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [assignmentForm, setAssignmentForm] = useState({
    user_id: '',
    comment: ''
  });

  // Document preview states
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const hiddenRenderContainerRef = useRef<HTMLDivElement>(null);
  const [fileArrayBuffer, setFileArrayBuffer] = useState<ArrayBuffer | null>(null);
  const [documentHtml, setDocumentHtml] = useState<string>('');
  const [editorContent, setEditorContent] = useState<string>('');
  const [leftPanelWidth, setLeftPanelWidth] = useState(400); // Default width 400px
  const [isResizing, setIsResizing] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Modal state untuk form dari ViewTemplate
  const [showModal, setShowModal] = useState(false);
  const [modalFormData, setModalFormData] = useState({
    title: '',
    description: '',
    pid: '',
    templateType: 'Internal',
    categoryId: '',
    agreementTypeId: '',
    templateId: ''
  });
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingAgreementTypes, setLoadingAgreementTypes] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [modalError, setModalError] = useState<string>('');

  // Check if user comes from ViewTemplate (mode: 'use')
  useEffect(() => {
    const state = location.state as any;
    if (state && state.mode === 'use' && state.template) {
      // User datang dari ViewTemplate, tampilkan modal
      setShowModal(true);
      setModalFormData({
        title: state.title || '',
        description: state.description || '',
        pid: state.pid || '',
        templateType: state.templateType || 'Internal',
        categoryId: String(state.categoryId || ''),
        agreementTypeId: String(state.agreementTypeId || ''),
        templateId: state.templateId ? String(state.templateId) : (state.template ? String(state.template.id) : '')
      });

      // Set template dari state langsung jika ada
      if (state.template) {
        setAvailableTemplates([state.template]);
      }

      // Load categories, agreement types, dan companies untuk modal
      const loadModalData = async () => {
        setLoadingCategories(true);
        setLoadingAgreementTypes(true);
        try {
          const [cats, types, comps] = await Promise.all([
            apiService.getCategories(),
            apiService.getAgreementTypes(),
            apiService.getCompanies()
          ]);
          setCategories(cats);
          setAgreementTypes(types);
          setCompanies(comps.filter((c: any) => c.is_active !== false));
        } catch (err: any) {
          setModalError('Gagal memuat data category, agreement type, dan companies');
          console.error('Error loading data:', err);
        } finally {
          setLoadingCategories(false);
          setLoadingAgreementTypes(false);
        }
      };
      loadModalData();
    }
  }, [location.state]);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        // Load data dari location.state (dari modal)
        const state = location.state as any;

        console.log('Location state:', state);

        // Skip jika user datang dari ViewTemplate (akan dihandle oleh modal)
        if (state && state.mode === 'use') {
          setLoading(false);
          return;
        }

        if (!state) {
          setError('Data tidak ditemukan. Silakan kembali ke halaman sebelumnya.');
          setLoading(false);
          return;
        }

        // Load categories, agreement types, companies, users, dan user groups secara parallel
        const [cats, types, comps, usersData, userGroupsData] = await Promise.all([
          apiService.getCategories(),
          apiService.getAgreementTypes(),
          apiService.getCompanies(),
          apiService.getUsers(),
          user?.id ? apiService.getUserGroups(user.id) : Promise.resolve([])
        ]);

        console.log('Loaded categories:', cats);
        console.log('Loaded agreement types:', types);

        // Set form data dari state - SET SETELAH categories dan agreementTypes di-load
        // Pastikan categoryId dan agreementTypeId adalah string untuk match dengan option value
        let categoryId = '';
        let agreementTypeId = '';

        if (state.categoryId) {
          categoryId = String(state.categoryId);
        }
        if (state.agreementTypeId) {
          agreementTypeId = String(state.agreementTypeId);
        }

        // Verify categoryId exists in loaded categories
        const categoryExists = cats.some((cat: any) => String(cat.id) === categoryId);
        const agreementTypeExists = types.some((type: any) => String(type.id) === agreementTypeId);

        console.log('Category exists check:', {
          categoryId,
          categoryExists,
          categories: cats.map((c: any) => ({ id: c.id, idString: String(c.id), name: c.name }))
        });
        console.log('Agreement type exists check:', {
          agreementTypeId,
          agreementTypeExists,
          agreementTypes: types.map((t: any) => ({ id: t.id, idString: String(t.id), name: t.name }))
        });

        // Set state untuk categories dan agreementTypes
        setCategories(cats);
        setAgreementTypes(types);
        setCompanies(comps.filter((c: any) => c.is_active !== false));
        setUsers(usersData.filter((u: any) => u.is_active !== false));

        // Set user groups
        if (userGroupsData && userGroupsData.length > 0) {
          const groupIds = userGroupsData.map((ug: any) => ug.group_id);
          setUserGroups(groupIds);
          console.log('User groups:', groupIds);
        }

        // Set form data SETELAH categories dan agreementTypes di-set ke state
        setFormData({
          title: state.title || '',
          description: state.description || '',
          pid: state.pid || '',
          templateType: state.templateType || 'Internal',
          categoryId: categoryId,
          agreementTypeId: agreementTypeId,
          companyId: '',
          templateId: state.templateId ? String(state.templateId) : (state.template ? String(state.template.id) : ''),
          agreementValue: state.agreementValue || '',
          paymentMethod: state.paymentMethod || '',
          period: state.period || '',
          guarantee: state.guarantee || ''
        });

        console.log('Form data set:', {
          title: state.title || '',
          description: state.description || '',
          categoryId,
          agreementTypeId,
          categoriesCount: cats.length,
          agreementTypesCount: types.length,
          categoryExists,
          agreementTypeExists
        });

        // Load template berdasarkan category_id dan agreement_type_id
        // Prioritas: gunakan template dari state jika ada, jika tidak load dari API
        let templateToLoad: any = null;

        // Set templateId dari state jika ada
        if (state.templateId) {
          setFormData(prev => ({ ...prev, templateId: String(state.templateId) }));
        }

        // Load template file untuk preview
        if (templateToLoad && templateToLoad.id) {
          await loadTemplateFile(templateToLoad.id);
        }
      } catch (err: any) {
        console.error('Error loading initial data:', err);
        setError('Gagal memuat data. Silakan refresh halaman.');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [location, user]);

  // Update formData categoryId dan agreementTypeId setelah categories dan agreementTypes ter-load
  useEffect(() => {
    if (categories.length > 0 && agreementTypes.length > 0 && location.state) {
      const state = location.state as any;
      if (state.categoryId && state.agreementTypeId) {
        const categoryId = String(state.categoryId);
        const agreementTypeId = String(state.agreementTypeId);

        // Only update if current formData doesn't match
        setFormData(prev => {
          if (prev.categoryId === categoryId && prev.agreementTypeId === agreementTypeId) {
            return prev; // No change needed
          }
          console.log('Updating formData from useEffect:', {
            oldCategoryId: prev.categoryId,
            newCategoryId: categoryId,
            oldAgreementTypeId: prev.agreementTypeId,
            newAgreementTypeId: agreementTypeId
          });
          return {
            ...prev,
            categoryId: categoryId,
            agreementTypeId: agreementTypeId
          };
        });
      }
    }
  }, [categories.length, agreementTypes.length, location.state]);

  // Load available templates saat categoryId, agreementTypeId, atau templateType berubah di main form
  useEffect(() => {
    if (!formData.categoryId || !formData.agreementTypeId) {
      setAvailableTemplatesMain([]);
      return;
    }

    const loadTemplates = async () => {
      setLoadingTemplatesMain(true);
      try {
        const templates = await apiService.getTemplates({
          category_id: Number(formData.categoryId),
          agreement_type_id: Number(formData.agreementTypeId),
          template_type: formData.templateType,
          is_active: 1
        });
        setAvailableTemplatesMain(templates || []);

        // Jika ada template dari state dan ada di list, set sebagai selected
        if (location.state && (location.state as any).templateId) {
          const stateTemplateId = (location.state as any).templateId;
          if (templates.find((t: any) => t.id === stateTemplateId)) {
            setFormData(prev => ({ ...prev, templateId: String(stateTemplateId) }));
          }
        }
      } catch (err: any) {
        console.error('Error loading templates:', err);
        setAvailableTemplatesMain([]);
      } finally {
        setLoadingTemplatesMain(false);
      }
    };

    // Debounce untuk menghindari terlalu banyak request
    const timeoutId = setTimeout(loadTemplates, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.categoryId, formData.agreementTypeId, formData.templateType]);

  // Load template saat templateId dipilih
  useEffect(() => {
    if (!formData.templateId) {
      setTemplate(null);
      setVariables([]);
      setFileArrayBuffer(null);
      setDocumentHtml('');
      setEditorContent('');
      return;
    }

    const loadSelectedTemplate = async () => {
      try {
        const fullTemplate = await apiService.getTemplate(Number(formData.templateId));
        setTemplate(fullTemplate);

        if (fullTemplate && fullTemplate.variables) {
          // Initialize variables dengan value kosong
          const initializedVariables = fullTemplate.variables.map((v: any) => ({
            ...v,
            value: ''
          }));
          setVariables(initializedVariables);
        }

        // Load template file untuk preview
        await loadTemplateFile(fullTemplate.id);
      } catch (err: any) {
        console.error('Error loading selected template:', err);
        setError('Gagal memuat template yang dipilih.');
      }
    };

    loadSelectedTemplate();
  }, [formData.templateId]);

  // Load available templates saat categoryId, agreementTypeId, atau templateType berubah di modal
  useEffect(() => {
    if (!showModal) return;

    const loadTemplates = async () => {
      if (!modalFormData.categoryId || !modalFormData.agreementTypeId) {
        setAvailableTemplates([]);
        setModalFormData(prev => ({ ...prev, templateId: '' }));
        return;
      }

      setLoadingTemplates(true);
      try {
        const templates = await apiService.getTemplates({
          category_id: Number(modalFormData.categoryId),
          agreement_type_id: Number(modalFormData.agreementTypeId),
          template_type: modalFormData.templateType,
          is_active: 1
        });
        setAvailableTemplates(templates || []);
        // Reset templateId jika template yang dipilih tidak ada di list baru
        if (modalFormData.templateId && !templates.find((t: any) => t.id === Number(modalFormData.templateId))) {
          setModalFormData(prev => ({ ...prev, templateId: '' }));
        }
      } catch (err: any) {
        console.error('Error loading templates:', err);
        setAvailableTemplates([]);
        setModalFormData(prev => ({ ...prev, templateId: '' }));
      } finally {
        setLoadingTemplates(false);
      }
    };

    // Debounce untuk menghindari terlalu banyak request
    const timeoutId = setTimeout(loadTemplates, 500);
    return () => clearTimeout(timeoutId);
  }, [modalFormData.categoryId, modalFormData.agreementTypeId, modalFormData.templateType, showModal]);

  // Modal handlers
  const handleModalInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setModalFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setModalError('');
  };

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalFormData.categoryId || !modalFormData.agreementTypeId || !modalFormData.title.trim() || !modalFormData.templateId) {
      setModalError('Silakan lengkapi semua field yang wajib diisi');
      return;
    }
    const selectedTemplate = availableTemplates.find(t => t.id === Number(modalFormData.templateId));
    if (!selectedTemplate) {
      setModalError('Template yang dipilih tidak valid. Silakan pilih template yang tersedia.');
      return;
    }
    setModalError('');
    setShowModal(false);

    // Prepare new state (tanpa mode: 'use')
    const newState = {
      categoryId: Number(modalFormData.categoryId),
      agreementTypeId: Number(modalFormData.agreementTypeId),
      templateType: modalFormData.templateType,
      title: modalFormData.title,
      description: modalFormData.description,
      pid: modalFormData.pid,
      category: categories.find(c => c.id === Number(modalFormData.categoryId)),
      agreementType: agreementTypes.find(t => t.id === Number(modalFormData.agreementTypeId)),
      templateId: selectedTemplate.id,
      template: selectedTemplate
    };

    // Update location state
    navigate('/new-request', {
      state: newState,
      replace: true
    });

    // Load data langsung dengan state baru
    setLoading(true);
    try {
      // Load categories, agreement types, companies, dan user groups secara parallel
      const [cats, types, comps, userGroupsData] = await Promise.all([
        apiService.getCategories(),
        apiService.getAgreementTypes(),
        apiService.getCompanies(),
        user?.id ? apiService.getUserGroups(user.id) : Promise.resolve([])
      ]);

      // Set state untuk categories dan agreementTypes
      setCategories(cats);
      setAgreementTypes(types);
      setCompanies(comps.filter((c: any) => c.is_active !== false));

      // Set user groups
      if (userGroupsData && userGroupsData.length > 0) {
        const groupIds = userGroupsData.map((ug: any) => ug.group_id);
        setUserGroups(groupIds);
      }

      // Set form data
      setFormData({
        title: newState.title || '',
        description: newState.description || '',
        pid: newState.pid || '',
        templateType: newState.templateType || 'Internal',
        categoryId: String(newState.categoryId),
        agreementTypeId: String(newState.agreementTypeId),
        companyId: '',
        templateId: newState.templateId ? String(newState.templateId) : (newState.template ? String(newState.template.id) : ''),
        agreementValue: '',
        paymentMethod: '',
        period: '',
        guarantee: ''
      });

      // Load template
      if (newState.template && newState.template.id) {
        try {
          const fullTemplate = await apiService.getTemplate(newState.template.id);
          setTemplate(fullTemplate);

          if (fullTemplate && fullTemplate.variables) {
            const initializedVariables = fullTemplate.variables.map((v: any) => ({
              ...v,
              value: ''
            }));
            setVariables(initializedVariables);
          }

          // Load template file untuk preview
          const token = localStorage.getItem('access_token');

          const response = await fetch(`${API_BASE_URL}/api/templates/${fullTemplate.id}/file?t=${new Date().getTime()}`, {
            headers: {
              ...(token && { 'Authorization': `Bearer ${token}` })
            }
          });

          if (response.ok) {
            const arrayBuffer = await response.arrayBuffer();
            setFileArrayBuffer(arrayBuffer);
          }
        } catch (err: any) {
          console.error('Error loading full template:', err);
          setTemplate(newState.template);
          if (newState.template.variables) {
            const initializedVariables = newState.template.variables.map((v: any) => ({
              ...v,
              value: ''
            }));
            setVariables(initializedVariables);
          }
          // Try to load template file
          if (newState.template.id) {
            try {
              const token = localStorage.getItem('access_token');

              const response = await fetch(`${API_BASE_URL}/api/templates/${newState.template.id}/file?t=${new Date().getTime()}`, {
                headers: {
                  ...(token && { 'Authorization': `Bearer ${token}` })
                }
              });

              if (response.ok) {
                const arrayBuffer = await response.arrayBuffer();
                setFileArrayBuffer(arrayBuffer);
              }
            } catch (fileErr: any) {
              console.error('Error loading template file:', fileErr);
            }
          }
        }
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError('Gagal memuat data. Silakan refresh halaman.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalFormData({
      title: '',
      description: '',
      pid: '',
      templateType: 'Internal',
      categoryId: '',
      agreementTypeId: '',
      templateId: ''
    });
    setModalError('');
    setAvailableTemplates([]);
    // Navigate back to templates
    navigate('/templates');
  };

  // Load template file dari backend
  const loadTemplateFile = async (templateId: number) => {
    try {
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}/file?t=${new Date().getTime()}`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error('Gagal memuat file template');
      }

      const arrayBuffer = await response.arrayBuffer();
      setFileArrayBuffer(arrayBuffer);
    } catch (err: any) {
      console.error('Error loading template file:', err);
      setError('Gagal memuat file template');
    }
  };

  // Initial load document HTML (hanya sekali saat fileArrayBuffer pertama kali di-load)
  useEffect(() => {
    if (!fileArrayBuffer || documentHtml) {
      return; // Skip if already loaded or no file
    }

    let isMounted = true;

    const loadDocument = async () => {
      let retryCount = 0;
      const maxRetries = 20;

      const checkAndLoad = async () => {
        if (!isMounted) {
          return;
        }

        // Use hidden container for renderAsync to avoid React conflicts
        const renderContainer = hiddenRenderContainerRef.current;

        if (!renderContainer) {
          retryCount++;
          if (retryCount < maxRetries) {
            setTimeout(checkAndLoad, 100);
            return;
          } else {
            console.error('Container tidak ditemukan setelah beberapa retry');
            return;
          }
        }

        try {
          // Clear previous content safely
          if (renderContainer) {
            renderContainer.innerHTML = '';
          }

          // Render document to hidden container
          await renderAsync(
            fileArrayBuffer,
            renderContainer,
            undefined,
            {
              className: 'docx-wrapper',
              inWrapper: true,
              ignoreWidth: false,
              ignoreHeight: false,
              breakPages: true,
              ignoreLastRenderedPageBreak: true,
              experimental: false,
              trimXmlDeclaration: true,
              debug: false
            }
          );

          // Wait a bit for renderAsync to finish all DOM manipulations
          await new Promise(resolve => setTimeout(resolve, 100));

          // Get initial HTML (without variable replacement yet)
          if (renderContainer && isMounted) {
            try {
              const htmlContent = renderContainer.innerHTML;
              setDocumentHtml(htmlContent);
              // Set initial editorContent tanpa variable replacement (akan di-render manual)
              setEditorContent(htmlContent);
            } catch (err) {
              console.warn('Error setting initial document HTML:', err);
            }
          }
        } catch (err) {
          console.error('Error loading document:', err);
          setError('Gagal memuat dokumen');
        }
      };

      setTimeout(checkAndLoad, 200);
    };

    loadDocument();

    return () => {
      isMounted = false;
    };
  }, [fileArrayBuffer]); // Only run when fileArrayBuffer changes

  // Function untuk render document dengan variable replacement (manual trigger)
  const handleRenderDocument = async () => {
    if (!documentHtml || !fileArrayBuffer) {
      setError('Dokumen belum dimuat. Silakan tunggu sebentar.');
      return;
    }

    try {
      // Create variables map untuk replacement
      const variablesMap: Record<string, string> = {};
      variables.forEach((v: any) => {
        if (v.value) {
          variablesMap[v.name] = v.value;
        }
      });

      // Replace variables dalam HTML string
      const replacedHtml = replaceVariablesInHTML(documentHtml, variablesMap);

      // Update state - React will handle the re-render via dangerouslySetInnerHTML
      setEditorContent(replacedHtml);
    } catch (err) {
      console.error('Error rendering document:', err);
      setError('Gagal merender dokumen');
    }
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.categoryId || !formData.agreementTypeId) {
      setError('Mohon lengkapi semua field yang wajib diisi');
      return;
    }

    if (supportingDocuments.length === 0) {
      setError('Mohon upload minimal satu dokumen pendukung');
      return;
    }



    setLoading(true);
    setError('');

    try {
      // Supporting documents sudah dalam format yang benar (array of objects dengan metadata)
      const uploadedDocuments = supportingDocuments.length > 0 ? supportingDocuments : null;

      // Prepare task data
      const taskData = {
        title: formData.title,
        description: formData.description || null,
        pid: formData.pid || null,
        template_type: formData.templateType || 'Internal',
        category_id: Number(formData.categoryId),
        agreement_type_id: Number(formData.agreementTypeId),
        company_id: formData.companyId ? Number(formData.companyId) : null,
        template_id: template?.id || null,
        agreement_amount: parseDigits(formData.agreementValue),
        payment_method: formData.paymentMethod || null,
        period: formData.period || null,
        guarantee: formData.guarantee || null,
        variables: variables.map((v: any) => ({
          name: v.name,
          position: v.position || 0,
          value: (v.value && v.value.trim() !== '') ? v.value : v.name.toLowerCase(),
          group_id: v.group_id || null
        })),
        supporting_documents: uploadedDocuments && uploadedDocuments.length > 0 ? uploadedDocuments : null,
        status_id: 1, // Default status: 1 (NEW)
        mentions: [] // Can be added later for mention feature
      };

      // Create task
      const createdTask = await apiService.createTask(taskData);

      console.log('Task created successfully:', createdTask);

      // Create assignment if user filled assignment form
      if (assignmentForm.user_id) {
        try {
          await apiService.createTaskAssignment({
            task_id: createdTask.id,
            user_id: Number(assignmentForm.user_id),
            comment: assignmentForm.comment || undefined
          });
        } catch (err: any) {
          console.error('Error creating assignment:', err);
          // Don't fail the whole process if assignment creation fails
        }
      }

      // Show success message and redirect
      alert('Agreement request berhasil dibuat!');
      navigate('/board');
    } catch (err: any) {
      console.error('Error creating task:', err);
      setError(err.message || 'Gagal membuat agreement request. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  // Helper untuk check apakah variable harus diisi oleh user ini
  const isVariableForCurrentUser = (variable: any): boolean => {
    if (!variable.group_id) return false; // Skip variables tanpa group
    if (!userGroups.length) return false;
    return userGroups.includes(variable.group_id);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVariableChange = (variableIndex: number, value: string) => {
    const updatedVariables = [...variables];
    updatedVariables[variableIndex] = {
      ...updatedVariables[variableIndex],
      value: value
    };
    setVariables(updatedVariables);
  };

  const handleRemoveDocument = (index: number) => {
    setSupportingDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadDocument = async (formData: UploadDocFormData) => {
    if (!formData.file) {
      throw new Error('Silakan pilih file untuk diupload');
    }

    if (!formData.reason) {
      throw new Error('Silakan isi alasan mengupload file ini');
    }

    if (formData.type === 'update' && !formData.changes) {
      throw new Error('Silakan isi perubahan yang dilakukan');
    }

    if (formData.type === 'update' && !formData.updated_from) {
      throw new Error('Silakan pilih dokumen yang diupdate');
    }

    // Upload file
    const uploadResult = await apiService.saveDocument(formData.file);

    // Prepare document metadata
    const newDoc: any = {
      file_path: uploadResult.file_path,
      file_name: uploadResult.file_name,
      saved_name: uploadResult.saved_name,
      size: formData.file.size,
      uploaded_by: {
        id: user?.id,
        name: user?.name,
        email: user?.email
      },
      uploaded_at: new Date().toISOString(),
      type: formData.type,
      reason: formData.reason,
      changes: formData.type === 'update' ? formData.changes : null,
      updated_from: formData.type === 'update' ? formData.updated_from : null
    };

    // Add new document
    setSupportingDocuments(prev => [...prev, newDoc]);
    setShowUploadDocModal(false);
  };

  // Handle resize left panel
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      // Min width 300px, max width 800px
      if (newWidth >= 300 && newWidth <= 800) {
        setLeftPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <button
              onClick={() => navigate('/board')}
              className="text-violet-600 hover:text-violet-700 flex items-center text-sm font-medium"
            >
              <i className="fa-solid fa-arrow-left mr-2"></i>
              Back to Agreements Board
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">New Agreement Request</h1>
              <p className="text-xs text-gray-600 mt-0.5">Submit a request for Legal to create a new agreement</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => navigate('/board')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="agreement-request-form"
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700"
            >
              Submit Request
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="px-6 py-3 bg-red-50 border-b border-red-200 text-red-700 text-sm flex-shrink-0">
          {error}
        </div>
      )}

      {/* Main Content - Split View */}
      <div className="flex-1 flex overflow-hidden" style={{ height: 'calc(100vh - 120px)' }}>
        {/* Left Panel - Form & Variables */}
        <div
          className="border-r border-gray-200 bg-white overflow-y-auto flex flex-col relative"
          style={{ width: `${leftPanelWidth}px`, minWidth: '300px', maxWidth: '800px' }}
        >
          {/* Resize Handle */}
          <div
            className="absolute right-0 top-0 bottom-0 w-1 bg-gray-300 hover:bg-violet-500 cursor-col-resize z-10"
            onMouseDown={(e) => {
              e.preventDefault();
              setIsResizing(true);
            }}
          />

          <div className="p-6 space-y-6">
            {error && (
              <div className="mb-6 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <form id="agreement-request-form" className="space-y-8" onSubmit={handleSubmit}>
              {/* Basic Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h2>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nama PT <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Masukkan Nama PT"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Masukkan description agreement (opsional)"
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PID
                    </label>
                    <input
                      type="text"
                      name="pid"
                      value={formData.pid}
                      onChange={handleInputChange}
                      placeholder="Masukkan PID (opsional)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Template Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="templateType"
                      value={formData.templateType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      required
                    >
                      <option value="Internal">Internal</option>
                      <option value="External">External</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Pilih jenis template yang akan digunakan. Internal untuk dokumen internal, External untuk dokumen eksternal.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category <span className="text-red-500">*</span>
                      </label>
                      <select
                        key={`category-select-${categories.length}-${formData.categoryId}`}
                        name="categoryId"
                        value={formData.categoryId ? String(formData.categoryId) : ''}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-gray-50"
                        required
                        disabled
                      >
                        <option value="">-- Pilih Category --</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={String(cat.id)}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                      {formData.categoryId && categories.length > 0 && !categories.find((cat: any) => String(cat.id) === String(formData.categoryId)) && (
                        <p className="text-xs text-red-500 mt-1">Category ID {formData.categoryId} tidak ditemukan dalam daftar</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Agreement Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        key={`agreement-type-select-${agreementTypes.length}-${formData.agreementTypeId}`}
                        name="agreementTypeId"
                        value={formData.agreementTypeId ? String(formData.agreementTypeId) : ''}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-gray-50"
                        required
                        disabled
                      >
                        <option value="">-- Pilih Agreement Type --</option>
                        {agreementTypes.map((type) => (
                          <option key={type.id} value={String(type.id)}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6 mt-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nilai Perjanjian
                      </label>
                      <RupiahInput
                        id="agreementValue"
                        value={parseDigits(formData.agreementValue)}
                        onChange={(val) => setFormData((prev: any) => ({ ...prev, agreementValue: val === null ? '' : String(val) }))}
                        placeholder="Masukkan nilai perjanjian (opsional)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Jangka Waktu
                      </label>
                      <input
                        type="text"
                        name="period"
                        value={formData.period}
                        onChange={handleInputChange}
                        placeholder="Masukkan jangka waktu (opsional)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cara Pembayaran
                      </label>
                      <textarea
                        name="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={handleInputChange}
                        placeholder="Masukkan cara pembayaran (opsional)"
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Jaminan
                      </label>
                      <textarea
                        name="guarantee"
                        value={formData.guarantee}
                        onChange={handleInputChange}
                        placeholder="Masukkan jaminan (opsional)"
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                      />
                    </div>
                  </div>

                </div>

                {/* Template Selection */}
                {formData.categoryId && formData.agreementTypeId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Template <span className="text-red-500">*</span>
                    </label>
                    {loadingTemplatesMain ? (
                      <div className="flex items-center space-x-2 text-gray-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                        <span>Memuat template yang tersedia...</span>
                      </div>
                    ) : availableTemplatesMain.length > 0 ? (
                      <select
                        name="templateId"
                        value={formData.templateId}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                        required
                      >
                        <option value="">-- Pilih Template --</option>
                        {availableTemplatesMain.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.title} {template.description ? `- ${template.description}` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <i className="fa-solid fa-exclamation-triangle text-yellow-600 mt-0.5"></i>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-yellow-800">
                              Tidak ada template yang tersedia untuk category, agreement type, dan template type ini.
                            </p>
                            <p className="text-xs text-yellow-700 mt-1">
                              Silakan buat template terlebih dahulu di halaman Templates.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Variables Section */}
              {variables.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Variables</h2>
                  <p className="text-sm text-gray-500 mb-6">
                    Semua variables dari template. Jika tidak diisi, nilai akan default ke nama variable (lowercase).
                  </p>

                  <div className="space-y-4">
                    {variables
                      .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
                      .map((variable: any, index: number) => {
                        const variableValue = variable.value || '';
                        const isForCurrentUser = isVariableForCurrentUser(variable);

                        return (
                          <VariableInput
                            key={`${variable.name}-${variable.position}`}
                            variable={variable}
                            isForCurrentUser={isForCurrentUser}
                            onUpdate={(newValue) => handleVariableChange(index, newValue)}
                          />
                        );
                      })}
                  </div>

                  {/* Render Document Button */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleRenderDocument}
                      disabled={!documentHtml || !fileArrayBuffer}
                      className="w-full px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      <i className="fa-solid fa-refresh mr-2"></i>
                      Update Document Preview
                    </button>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Klik tombol di atas untuk memperbarui preview dokumen dengan nilai variable yang sudah diisi
                    </p>
                  </div>
                </div>
              )}

              {/* Supporting Documents */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Supporting Documents <span className="text-red-500">*</span></h2>
                  <button
                    type="button"
                    onClick={() => setShowUploadDocModal(true)}
                    className="px-3 py-1.5 text-sm font-medium text-violet-600 bg-violet-50 rounded-lg hover:bg-violet-100 transition-colors"
                  >
                    <i className="fa-solid fa-plus mr-1"></i>
                    Tambah Dokumen
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Upload dokumen pendukung untuk agreement ini (Wajib minimal 1 file)
                </p>

                {/* Document List */}
                {supportingDocuments.length > 0 ? (
                  <div className="space-y-3">
                    {supportingDocuments.map((doc: any, index: number) => (
                      <div
                        key={index}
                        className="p-3 bg-gray-50 border border-gray-200 rounded-lg"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <i className="fa-solid fa-file text-gray-400"></i>
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {doc.file_name || doc.name || 'Document'}
                              </p>
                            </div>
                            <div className="space-y-1 text-xs text-gray-600">
                              <p>
                                <span className="font-medium">Type:</span>{' '}
                                <span className={`px-2 py-0.5 rounded ${doc.type === 'new'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-blue-100 text-blue-700'
                                  }`}>
                                  {doc.type === 'new' ? 'Baru' : 'Update'}
                                </span>
                              </p>
                              <p><span className="font-medium">Alasan:</span> {doc.reason}</p>
                              {doc.type === 'update' && doc.changes && (
                                <p><span className="font-medium">Perubahan:</span> {doc.changes}</p>
                              )}
                              {doc.type === 'update' && doc.updated_from && (
                                <p><span className="font-medium">Update dari:</span> {doc.updated_from}</p>
                              )}
                              {doc.size && (
                                <p><span className="font-medium">Size:</span> {(doc.size / 1024 / 1024).toFixed(2)} MB</p>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveDocument(index)}
                            className="ml-4 text-red-600 hover:text-red-700"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                    <i className="fa-solid fa-file text-gray-400 text-3xl mb-2"></i>
                    <p className="text-sm text-gray-500">Belum ada dokumen pendukung</p>
                  </div>
                )}
              </div>

              {/* Assignment Section */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Assignment</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assign to
                    </label>
                    <select
                      value={assignmentForm.user_id}
                      onChange={(e) => setAssignmentForm(prev => ({ ...prev, user_id: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    >
                      <option value="">-- Pilih User (Opsional) --</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Comment
                    </label>
                    <AssignmentCommentInput
                      value={assignmentForm.comment}
                      onChange={(value) => setAssignmentForm(prev => ({ ...prev, comment: value }))}
                      placeholder="Masukkan komentar (opsional)"
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => navigate('/board')}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-violet-500 flex items-center"
                  >
                    {loading ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                        Menyimpan...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-save mr-2"></i>
                        Submit
                      </>
                    )}
                  </button>
                </div>
                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Right Panel - Document Preview */}
        <div className="flex-1 overflow-hidden flex flex-col bg-gray-100">
          <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
            <h2 className="text-sm font-semibold text-gray-700">Document Preview</h2>
            {template && (
              <p className="text-xs text-gray-500 mt-1">File: {template.file_name}</p>
            )}
          </div>

          <div className="flex-1 overflow-hidden">
            {/* Hidden container for renderAsync to avoid React conflicts */}
            <div
              ref={hiddenRenderContainerRef}
              style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }}
            />

            <div className="h-full overflow-y-auto overflow-x-hidden bg-white" style={{ paddingLeft: '40px', paddingRight: '40px' }}>
              {!fileArrayBuffer && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <i className="fa-solid fa-file-word text-gray-400 text-4xl mb-4"></i>
                    <p className="text-gray-600">Dokumen akan muncul setelah template dimuat</p>
                  </div>
                </div>
              )}
              {fileArrayBuffer && (editorContent || documentHtml) && (
                <div
                  key={`preview-${JSON.stringify(variables.map(v => ({ name: v.name, value: v.value })))}`}
                  ref={previewContainerRef}
                  dangerouslySetInnerHTML={{ __html: editorContent || documentHtml }}
                  style={{
                    minHeight: 0,
                    width: '100%',
                    maxWidth: '100%'
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal New Request (untuk user dari ViewTemplate) */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">New Agreement Request</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fa-solid fa-times text-lg"></i>
              </button>
            </div>

            <form onSubmit={handleModalSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nama PT <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={modalFormData.title}
                  onChange={handleModalInputChange}
                  placeholder="Ketik atau pilih nama PT"
                  list="modal-companies-list"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  required
                />
                <datalist id="modal-companies-list">
                  {companies.map((company) => (
                    <option key={company.id} value={company.name} />
                  ))}
                </datalist>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={modalFormData.description}
                  onChange={handleModalInputChange}
                  placeholder="Masukkan description agreement (opsional)"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PID
                </label>
                <input
                  type="text"
                  name="pid"
                  value={modalFormData.pid}
                  onChange={handleModalInputChange}
                  placeholder="Masukkan PID (opsional)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="templateType"
                  value={modalFormData.templateType}
                  onChange={handleModalInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  required
                >
                  <option value="Internal">Internal</option>
                  <option value="External">External</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Pilih jenis template: Internal untuk dokumen internal, External untuk dokumen eksternal
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                {loadingCategories ? (
                  <div className="flex items-center space-x-2 text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                    <span>Memuat categories...</span>
                  </div>
                ) : (
                  <select
                    name="categoryId"
                    value={modalFormData.categoryId}
                    onChange={handleModalInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    required
                    disabled={!!(location.state as any)?.categoryId}
                  >
                    <option value="">-- Pilih Category --</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Agreement Type <span className="text-red-500">*</span>
                </label>
                {loadingAgreementTypes ? (
                  <div className="flex items-center space-x-2 text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                    <span>Memuat agreement types...</span>
                  </div>
                ) : (
                  <select
                    name="agreementTypeId"
                    value={modalFormData.agreementTypeId}
                    onChange={handleModalInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    required
                    disabled={!!(location.state as any)?.agreementTypeId}
                  >
                    <option value="">-- Pilih Agreement Type --</option>
                    {agreementTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Template Selection */}
              {modalFormData.categoryId && modalFormData.agreementTypeId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template <span className="text-red-500">*</span>
                  </label>
                  {loadingTemplates ? (
                    <div className="flex items-center space-x-2 text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                      <span>Memuat template yang tersedia...</span>
                    </div>
                  ) : availableTemplates.length > 0 ? (
                    <select
                      name="templateId"
                      value={modalFormData.templateId}
                      onChange={handleModalInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      required
                    >
                      <option value="">-- Pilih Template --</option>
                      {availableTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.title} {template.description ? `- ${template.description}` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <i className="fa-solid fa-exclamation-triangle text-yellow-600 mt-0.5"></i>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-yellow-800">
                            Tidak ada template yang tersedia untuk category, agreement type, dan template type ini.
                          </p>
                          <p className="text-xs text-yellow-700 mt-1">
                            Silakan buat template terlebih dahulu di halaman Templates.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {modalError && (
                <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {modalError}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={!modalFormData.templateId || loadingTemplates || availableTemplates.length === 0}
                  className="px-6 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      <UploadDocumentModal
        isOpen={showUploadDocModal}
        onClose={() => setShowUploadDocModal(false)}
        onUpload={handleUploadDocument}
        existingDocuments={supportingDocuments}
      />
    </div>
  );
};

export default NewAgreementRequest;
